import { useQueryClient } from '@tanstack/react-query';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageMeta from '../../components/common/PageMeta';
import { useAuth } from '../../context/AuthContext';
import {
  type Checklist,
  type ChecklistItem,
  ITEM_STATUS_LABEL,
  type PhaseView,
  STORE_STATUS_LABEL,
  itemStatusTone,
  nextStep,
  phaseViews,
  sinceLiveLine,
  storeStatusTone,
  stepsLeft,
  stepsSinceLive,
  summaryLine,
} from '../../features/onboarding/onboardingChecklist';
import {
  useOnboardingChecklist,
  useStripeConnect,
} from '../../features/onboarding/useOnboardingChecklist';
import { useScopedQueryKey } from '../../hooks/useScopedQueryKey';
import {
  BlockSkeleton,
  Button,
  Callout,
  EmptyState,
  IconArrowRight,
  IconChevronDown,
  IconChevronLeft,
  IconClock,
  IconWarning,
  JourneyPhase,
  JourneyProgress,
  JourneyRow,
  Page,
  Spinner,
  StatusLabel,
  StatusPill,
  StepCheck,
  StepRing,
  useJustCompleted,
} from '../../ui/v2';
import GoLiveStep from './steps/GoLiveStep';
import { type StepProps, StepFlowProvider } from './steps/StepFlow';
import TestStep from './steps/TestStep';
import {
  ContactsStep,
  DocumentsStep,
  HoursStep,
  OpenTableStep,
  OrderingStep,
  StripeStep,
  TransferLineStep,
} from './steps/setupSteps';

type Stripe = ReturnType<typeof useStripeConnect>;

/** What opens each step, on its row while the step is closed. */
const OPEN_LABEL: Record<string, string> = {
  hours: 'Set hours',
  documents: 'Upload documents',
  contacts: 'Add contacts',
  transfer_line: 'Add a number',
  opentable: 'Set up',
  ordering_access: 'Give us access',
  stripe: 'Set up',
  test: 'Start testing',
  go_live: 'Go live',
};

/** Every step is done in place; these are the panels. */
const PANELS: Record<string, React.FC<StepProps & { stripe: Stripe }>> = {
  hours: HoursStep,
  documents: DocumentsStep,
  contacts: ContactsStep,
  transfer_line: TransferLineStep,
  opentable: OpenTableStep,
  ordering_access: OrderingStep,
  stripe: StripeStep,
  test: () => <TestStep />,
  go_live: ({ checklist }) => <GoLiveStep checklist={checklist} />,
};

/**
 * Whether a step opens. Locked steps (go live before everything else) and
 * steps waiting on Ask Arthur have nothing for the store to do yet; going
 * live opens again once it's confirmed, for the forwarding steps.
 */
const opens = (item: ChecklistItem, checklist: Checklist) => {
  if (!PANELS[item.key]) return false;
  if (item.key === 'go_live')
    return item.status !== 'locked' || Boolean(checklist.go_live_confirmed_at);
  return item.status !== 'locked' && item.status !== 'waiting';
};

/** How long a finished step stays open, saying what's next, before it moves on. */
export const ADVANCE_MS = 1600;
/** How long the finished step's fold runs before the next one opens (tokens.css). */
const FOLD_MS = 300;

/**
 * The step to move to after `from`. The flow keeps going down the page: the
 * next step still to do below `from`, else one left above it, else an
 * optional step that's still open, else none. Once live, only steps added
 * since are left to move to.
 */
const nextAfter = (checklist: Checklist, from: string) => {
  const index = checklist.items.findIndex((item) => item.key === from);
  const since =
    checklist.status === 'live'
      ? new Set(stepsSinceLive(checklist).map((item) => item.key))
      : null;
  const ordered = [
    ...checklist.items.slice(index + 1),
    ...checklist.items.slice(0, Math.max(index, 0)),
  ].filter(
    (item) =>
      item.key !== from &&
      opens(item, checklist) &&
      (!since || since.has(item.key)),
  );
  return (
    ordered.find((item) => item.status === 'todo') ??
    ordered.find((item) => item.status === 'recommended') ??
    null
  );
};

const reducedMotion = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * The steps save through their own Store info queries, not the checklist.
 * While a step is open, any of those finishing a save (a refetch or a
 * write, not a first load) refreshes the checklist, so the row ticks as
 * soon as the store is done. The onboarding queries themselves (the test
 * calls, the forwarding check) poll and are left out.
 */
function useChecklistFollowsSaves(active: boolean) {
  const client = useQueryClient();
  const key = useScopedQueryKey('onboarding-checklist');
  const keyRef = useRef(key);
  keyRef.current = key;
  useEffect(() => {
    if (!active) return;
    let timer: number | undefined;
    const unsubscribe = client.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated' || event.action.type !== 'success') return;
      const base = event.query.queryKey[0];
      if (typeof base === 'string' && base.startsWith('onboarding-')) return;
      if (event.query.state.dataUpdateCount < 2 && !event.action.manual) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(
        () => void client.invalidateQueries({ queryKey: keyRef.current }),
        400,
      );
    });
    return () => {
      unsubscribe();
      window.clearTimeout(timer);
    };
  }, [active, client]);
}

const ItemGlyph: React.FC<{
  status: ChecklistItem['status'];
  fresh: boolean;
}> = ({ status, fresh }) => {
  if (status === 'done')
    return <StepCheck animate={fresh} className="text-v2-success" />;
  if (status === 'waiting')
    return <IconClock size={20} className="text-v2-warning" />;
  if (status === 'recommended')
    return <IconWarning size={20} className="text-v2-warning" />;
  return <StepRing tone={status === 'locked' ? 'muted' : 'default'} />;
};

/**
 * The row's button. Closed, it opens the step: the step the server names
 * next gets the page's one primary, other open steps are tonal, finished
 * ones a quiet Edit. Open, it closes it. While any step is open, its own
 * button is the primary, so every closed row's button is tonal.
 */
const ItemAction: React.FC<{
  item: ChecklistItem;
  checklist: Checklist;
  next: boolean;
  open: boolean;
  anotherOpen: boolean;
  onToggle: () => void;
}> = ({ item, checklist, next, open, anotherOpen, onToggle }) => {
  if (!opens(item, checklist)) return null;
  if (open) {
    return (
      <Button
        variant="ghost"
        trailing={<IconChevronDown size={14} className="rotate-180" />}
        onClick={onToggle}
      >
        Close
      </Button>
    );
  }
  if (item.status === 'done') {
    return (
      <Button variant="ghost" onClick={onToggle}>
        {item.key === 'test' ? 'Test again' : 'Edit'}
      </Button>
    );
  }
  const label =
    item.key === 'go_live' && checklist.go_live_confirmed_at
      ? 'Continue'
      : OPEN_LABEL[item.key];
  return (
    <Button
      variant={
        (next || item.key === 'go_live') && !anotherOpen ? 'primary' : 'tonal'
      }
      trailing={next ? <IconArrowRight size={14} /> : undefined}
      onClick={onToggle}
    >
      {label}
    </Button>
  );
};

/** The moment between a step finishing and the next one opening. */
const Advancing: React.FC<{
  next: ChecklistItem;
  onStay: () => void;
}> = ({ next, onStay }) => (
  <div className="v2-rise-in mt-6 overflow-hidden rounded-v2-lg border border-v2-success/25 bg-v2-success/[0.06]">
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5">
      <p role="status" className="text-v2-13 text-v2-fg">
        <span className="font-semibold text-v2-success">Done.</span> Up next:{' '}
        {next.title}
      </p>
      <Button size="sm" variant="ghost" onClick={onStay}>
        Stay here
      </Button>
    </div>
    <div
      aria-hidden
      className="v2-countdown h-0.5 bg-v2-success/50"
      style={{ '--v2-countdown': `${ADVANCE_MS}ms` } as React.CSSProperties}
    />
  </div>
);

/** An optional step can be left for later; the next one is a click away. */
const SkipFooter: React.FC<{
  next: ChecklistItem;
  onSkip: () => void;
}> = ({ next, onSkip }) => (
  <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-v2-line/80 pt-4">
    <p className="text-v2-12 text-v2-fg-3">
      Optional. You can come back to this any time.
    </p>
    <Button
      variant="tonal"
      trailing={<IconArrowRight size={14} />}
      onClick={onSkip}
    >
      Skip to {next.title}
    </Button>
  </div>
);

interface Flow {
  openKey: string | null;
  toggle: (key: string) => void;
  advancing: { from: string; to: ChecklistItem } | null;
  stay: () => void;
  goTo: (key: string) => void;
  rowRef: (key: string) => (el: HTMLLIElement | null) => void;
  stripe: Stripe;
}

const ItemRow: React.FC<{
  item: ChecklistItem;
  checklist: Checklist;
  flow: Flow;
  next: boolean;
  fresh: boolean;
}> = ({ item, checklist, flow, next, fresh }) => {
  const open = flow.openKey === item.key;
  const Panel = PANELS[item.key];
  const expandable = opens(item, checklist);
  const following =
    item.status === 'recommended' ? nextAfter(checklist, item.key) : null;
  const toggle = () => flow.toggle(item.key);
  return (
    <JourneyRow
      rowRef={flow.rowRef(item.key)}
      glyph={<ItemGlyph status={item.status} fresh={fresh} />}
      title={item.title}
      // Open, the step says it all; the line would only repeat it.
      detail={(!open && item.detail) || undefined}
      note={item.note ? `Note from Ask Arthur: ${item.note}` : undefined}
      muted={item.status === 'done' || item.status === 'waiting'}
      justDone={fresh}
      status={
        // A step's own button already says it's to do.
        item.status !== 'locked' &&
        !(item.status === 'todo' && expandable) && (
          <StatusPill
            tone={itemStatusTone(item.status)}
            hollow={item.status === 'waiting'}
          >
            {ITEM_STATUS_LABEL[item.status]}
          </StatusPill>
        )
      }
      action={
        <ItemAction
          item={item}
          checklist={checklist}
          next={next}
          open={open}
          anotherOpen={flow.openKey !== null && !open}
          onToggle={toggle}
        />
      }
      open={open}
      onToggle={expandable ? toggle : undefined}
      panel={
        expandable && Panel ? (
          <>
            <Panel item={item} checklist={checklist} stripe={flow.stripe} />
            {flow.advancing?.from === item.key ? (
              <Advancing next={flow.advancing.to} onStay={flow.stay} />
            ) : (
              following && (
                <SkipFooter
                  next={following}
                  onSkip={() => flow.goTo(following.key)}
                />
              )
            )}
          </>
        ) : undefined
      }
    />
  );
};

const phaseMeta = (view: PhaseView) => {
  if (view.state === 'done') {
    return view.recommended
      ? `All done · ${view.recommended} recommended`
      : 'All done';
  }
  const parts = [`${view.progress.done} of ${view.progress.total} done`];
  if (view.waiting) parts.push(`${view.waiting} waiting on Ask Arthur`);
  return parts.join(' · ');
};

const PhaseCard: React.FC<{
  view: PhaseView;
  checklist: Checklist;
  flow: Flow;
  fresh: ReadonlySet<string>;
  justDone: boolean;
}> = ({ view, checklist, flow, fresh, justDone }) => (
  <JourneyPhase
    number={view.number}
    label={view.label}
    state={view.state}
    index={view.number}
    meta={phaseMeta(view)}
    unlockHint={view.unlockHint}
    celebrate={justDone}
    // A finished phase stays open while one of its steps is.
    holdOpen={view.items.some((item) => item.key === flow.openKey)}
    preview={view.items.map((item) => item.title).join(' · ')}
  >
    <ul>
      {view.items.map((item) => (
        <ItemRow
          key={item.key}
          item={item}
          checklist={checklist}
          flow={flow}
          next={nextStep(checklist)?.key === item.key}
          fresh={fresh.has(item.key)}
        />
      ))}
    </ul>
  </JourneyPhase>
);

const description = (checklist: Checklist) => {
  if (checklist.status === 'live')
    return stepsSinceLive(checklist).length
      ? `Your agent is answering calls for ${checklist.store_name}. Finish the steps below for what you’ve added since.`
      : `Your agent is answering calls for ${checklist.store_name}.`;
  if (checklist.status === 'ready')
    return 'Everything’s set. Forwarding your number is the last step.';
  // The steps below say what setting up means; this line only sets expectations.
  return 'About 10 minutes. Forwarding your number comes last.';
};

/**
 * Back from Stripe's onboarding (`?stripe=return`): ask the server whether
 * the account can take payments now. `?stripe=refresh` means Stripe's link
 * stopped working: they're single-use and last minutes, so a reload or the
 * back button lands here too. A fresh link carries on where the store was,
 * so it goes straight back without asking.
 */
function useStripeReturn(stripe: Stripe): 'relinking' | 'failed' | null {
  const [params, setParams] = useSearchParams();
  const outcome = params.get('stripe');
  const handled = useRef(false);
  const { refresh, start } = stripe;
  const [relink, setRelink] = useState<'relinking' | 'failed' | null>(null);

  useEffect(() => {
    if ((outcome !== 'return' && outcome !== 'refresh') || handled.current)
      return;
    handled.current = true;
    if (outcome === 'return') {
      refresh.mutate();
    } else {
      setRelink('relinking');
      start.mutate(undefined, {
        onSuccess: (url) => {
          window.location.href = url;
        },
        onError: () => setRelink('failed'),
      });
    }
    setParams(
      (current) => {
        current.delete('stripe');
        return current;
      },
      { replace: true },
    );
  }, [outcome, refresh, start, setParams]);

  return relink;
}

/**
 * Which step is open, kept in `?step=` so a reload, a link from elsewhere
 * (the dashboard's Go live) or Stripe's return lands on it. With none
 * named, the server's next step opens, so the page starts where the store
 * left off.
 */
function useOpenStep(checklist: Checklist, fromStripe: boolean) {
  const [params, setParams] = useSearchParams();
  const requested = params.get('step');
  const [openKey, setOpenKey] = useState<string | null>(() => {
    const wanted = requested ?? (fromStripe ? 'stripe' : null);
    const named = checklist.items.find((item) => item.key === wanted);
    if (named && opens(named, checklist)) return named.key;
    const next = checklist.items.find(
      (item) => item.key === nextStep(checklist)?.key,
    );
    return next && opens(next, checklist) ? next.key : null;
  });

  useEffect(() => {
    if ((params.get('step') ?? null) === openKey) return;
    setParams(
      (current) => {
        if (openKey) current.set('step', openKey);
        else current.delete('step');
        return current;
      },
      { replace: true },
    );
  }, [openKey, params, setParams]);

  return [openKey, setOpenKey] as const;
}

const Journey: React.FC<{
  checklist: Checklist;
  stripe: Stripe;
  fromStripe: boolean;
}> = ({ checklist, stripe, fromStripe }) => {
  const views = useMemo(() => phaseViews(checklist), [checklist]);
  const doneItems = useMemo(
    () =>
      checklist.items
        .filter((item) => item.status === 'done')
        .map((item) => item.key),
    [checklist.items],
  );
  const donePhases = useMemo(
    () =>
      views
        .filter((view) => view.state === 'done')
        .map((view) => `phase:${view.phase}`),
    [views],
  );
  const freshItems = useJustCompleted(doneItems);
  const freshPhases = useJustCompleted(donePhases);
  const live = checklist.status === 'live';

  const [openKey, setOpenKey] = useOpenStep(checklist, fromStripe);
  const [advancing, setAdvancing] = useState<Flow['advancing']>(null);
  useChecklistFollowsSaves(openKey !== null);

  // Rows by key, to bring an opened step into view.
  const rows = useRef(new Map<string, HTMLLIElement>());
  const refs = useRef(new Map<string, (el: HTMLLIElement | null) => void>());
  const rowRef = useCallback((key: string) => {
    let ref = refs.current.get(key);
    if (!ref) {
      ref = (el) => {
        if (el) rows.current.set(key, el);
        else rows.current.delete(key);
      };
      refs.current.set(key, ref);
    }
    return ref;
  }, []);

  /**
   * After the step above has had time to fold, scroll the opened one to
   * the top unless it's already comfortably in view.
   */
  const reveal = useCallback((key: string) => {
    window.setTimeout(() => {
      const row = rows.current.get(key);
      if (!row || typeof row.scrollIntoView !== 'function') return;
      const top = row.getBoundingClientRect().top;
      if (top >= 0 && top <= window.innerHeight * 0.4) return;
      row.scrollIntoView({
        behavior: reducedMotion() ? 'auto' : 'smooth',
        block: 'start',
      });
    }, 320);
  }, []);

  const goTo = useCallback(
    (key: string) => {
      setAdvancing(null);
      setOpenKey(key);
      reveal(key);
    },
    [reveal, setOpenKey],
  );
  const toggle = useCallback(
    (key: string) => {
      setAdvancing(null);
      if (openKey === key) {
        setOpenKey(null);
        return;
      }
      setOpenKey(key);
      reveal(key);
    },
    [openKey, reveal, setOpenKey],
  );

  // The open step finishing: say what's next, then move on to it.
  const statuses = useRef(
    new Map(checklist.items.map((item) => [item.key, item.status])),
  );
  useEffect(() => {
    const before = statuses.current;
    statuses.current = new Map(
      checklist.items.map((item) => [item.key, item.status]),
    );
    if (!openKey || before.get(openKey) === 'done') return;
    const now = checklist.items.find((item) => item.key === openKey);
    if (now?.status !== 'done') return;
    const to = nextAfter(checklist, openKey);
    if (to) setAdvancing({ from: openKey, to });
  }, [checklist, openKey]);
  // One sequence: the check draws while "Done" shows, the finished row
  // folds closed, then the next step opens and scrolls into view.
  useEffect(() => {
    if (!advancing) return;
    const timers: number[] = [];
    timers.push(
      window.setTimeout(() => {
        setOpenKey(null);
        timers.push(
          window.setTimeout(
            () => goTo(advancing.to.key),
            reducedMotion() ? 0 : FOLD_MS,
          ),
        );
      }, ADVANCE_MS),
    );
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [advancing, goTo, setOpenKey]);

  // Once in view on arrival, the step named in the link or opened first.
  const arrived = useRef(false);
  useEffect(() => {
    if (arrived.current || !openKey) return;
    arrived.current = true;
    reveal(openKey);
  }, [openKey, reveal]);

  const flow: Flow = {
    openKey,
    toggle,
    advancing,
    stay: () => setAdvancing(null),
    goTo,
    rowRef,
    stripe,
  };
  const stepFlow = useMemo(
    () => ({ open: goTo, close: () => setOpenKey(null) }),
    [goTo, setOpenKey],
  );

  return (
    <StepFlowProvider value={stepFlow}>
      <JourneyProgress
        done={checklist.counts.done}
        total={checklist.counts.total}
        summary={
          live
            ? sinceLiveLine(stepsLeft(checklist))
            : summaryLine(checklist.counts)
        }
        status={
          <StatusLabel tone={storeStatusTone(checklist.status)}>
            {STORE_STATUS_LABEL[checklist.status]}
          </StatusLabel>
        }
        className="v2-enter"
      />
      {views.map((view) => (
        <PhaseCard
          key={view.phase}
          view={view}
          checklist={checklist}
          flow={flow}
          fresh={freshItems}
          justDone={freshPhases.has(`phase:${view.phase}`)}
        />
      ))}
    </StepFlowProvider>
  );
};

export default function GettingStartedPage() {
  const checklist = useOnboardingChecklist({ fresh: true });
  const stripe = useStripeConnect();
  const { user } = useAuth();
  const navigate = useNavigate();
  // The group Onboarding grid links in here; give its users the way back.
  const seesGroup =
    user?.is_platform_admin === true ||
    (user?.onboarding_groups?.length ?? 0) > 0;
  const [params] = useSearchParams();
  // Read once: the Stripe return takes its parameter off the URL.
  const [fromStripe] = useState(() => params.has('stripe'));
  const relink = useStripeReturn(stripe);
  const data = checklist.data;
  const error = stripe.refresh.error;

  let body: React.ReactNode;
  if (checklist.isPending) {
    body = <BlockSkeleton rows={6} />;
  } else if (checklist.isError || !data) {
    body = (
      <Callout tone="danger">
        We couldn’t load your setup checklist. Refresh the page to try again.
      </Callout>
    );
  } else if (!data.enabled) {
    body = (
      <EmptyState
        title="Nothing to set up here yet"
        description="Your onboarding lead will walk you through setup."
      />
    );
  } else {
    body = (
      <>
        {error && <Callout tone="danger">{(error as Error).message}</Callout>}
        {relink === 'relinking' && (
          <Callout tone="neutral">
            <span role="status" className="flex items-center gap-2">
              <Spinner className="text-v2-accent" />
              Taking you back to Stripe…
            </span>
          </Callout>
        )}
        {relink === 'failed' && (
          <Callout tone="warning">
            We couldn’t reopen Stripe. Choose Connect with Stripe to carry on
            where you left off.
          </Callout>
        )}
        <Journey checklist={data} stripe={stripe} fromStripe={fromStripe} />
      </>
    );
  }

  // V2 pages own their scrolling, and the steps open in place, so the page
  // runs well past the fold.
  return (
    <div className="v2-scroll min-h-0 flex-1 overflow-y-auto">
      <Page
        fill
        title="Getting started"
        description={data?.enabled ? description(data) : undefined}
        actions={
          seesGroup && (
            <Button
              variant="ghost"
              size="sm"
              leading={<IconChevronLeft size={14} />}
              onClick={() => navigate('/onboarding')}
            >
              All locations
            </Button>
          )
        }
        className="max-w-[920px]"
      >
        <PageMeta
          title="AskArthur | Getting started"
          description="What’s left before your agent answers calls"
        />
        {body}
      </Page>
    </div>
  );
}
